// This is a part of the Cadcorp SIS source code. 
// Copyright (c) NEC Software Solutions UK Limited 1991-2026 
import { Location } from 'vue-router';
/**
* An interface to describe configuration for an event object
* @interface
* @public
*/
export interface IEventConfig {
    /**
    * @public
    * @property {object} owner The owner of the event object
    */
    owner: object;
}
/**
* An interface to describe a source for events
* @interface
* @public
*/
export interface IEventSource {
    /** Register an event on the source.
    * When the event is dispatched, the handler function will be called.
    * @public
    * @function
    * @param {string} type - The event type
    * @param {function} handler - The function that will be called when the event is triggered
    */
    on(type: string, listener: (eventData: any) => boolean);
    /** UnRegister an event on the source.
    * To unregister an event, the type and handler must the the same as when on was called
    * @public
    * @function
    * @param {string} type - The event type
    * @param {function} handler - The function that will be called when the event is triggered
    */
    un(type: string, listener: (eventData: any) => boolean);
}
/**
* An interface to describe a Schema Column
* @interface
* @public
*/
export interface ISchemaColumn {
    /**
    * @public
    * @property {string} name The name of this schema column, retrieved from the _description$ property
    */
    name: string;
    /**
    * @public
    * @property {string} expression The expression of this schema column, retrieved from the _formula$ property
    */
    expression: string;
    /**
    * @public
    * @property {boolean} isMapTip True if this column is defined as a maptip, otherwise false. Retrieved from the _bMapTip& property
    */
    isMapTip: boolean;
    /**
    * @public
    * @property {boolean} isHidden True if this column is hidden, otherwise false. Retrieved from the _bHidden& property
    */
    isHidden: boolean;

    /**
    * @public
    * @property {number} type The type of this schema column. Retrieved from the _type& property
    */
    type: number;

    /**
    * @public
    * @property {string} typeName The name for the type of this schema column. Retrieved from the _typeName$ property
    */
    typeName: string;
    /**
    * @property {number} columnIndex - The index of this schema
    * @public
    */
    columnIndex: number;
}
export interface IExpressionBuilder {
    /**
    * Creates a SIS expresion to query a http layer with, expected to be overridden in child classes
    * @export
    * @param {ol.Map} map The open layers map
    * @returns {string} A sis expression
    */
    createExpression(map) : string ;
}
export interface IFilterExpressionBuilder extends IExpressionBuilder {
    /**
     * Creates a SIS expression to filter based on a column and value. Extends IExpressionBuilder
     * @param {ISchemaColumn} schemaColumn Schema column to search
     * @param {string} uid Value to search
     */
    setExpressionData(schemaColumn: ISchemaColumn, uid: string): void;
}
/**
* An interface to ajax operations
* @interface
* @public
*/
export interface IAjax {
    /**
     * Perform a get request
     * @param {string} endpoint The url to perform the operation on
     * @param {object} [options] The options to apply to this request, they will be merged with the default options
     * @return {Promise} A promise containing the request
     */
    get(endpoint : string, options?: object) :Promise<any>;
    /**
     * Perform a post request
     * @param {string} endpoint The url to perform the operation on
     * @param {object} [data] A data object to send as the body of the request
     * @param {object} [options] The options to apply to this request, they will be merged with the default options
     * @return {Promise} A promise containing the request
     */
    post(endpoint: string, data?: object, options?: object): Promise<any>;

    /**
     * Perform a post request
     * @param {string} endpoint The url to perform the operation on
     * @param {FormData} [data] Form data to send
     * @param {object} [options] The options to apply to this request, they will be merged with the default options
     * @return {Promise} A promise containing the request
     */
    upload(endpoint: string, data?: object, options?: object): Promise<any>;

    /**
     * Perform a post request
     * @param {string} endpoint The url to perform the operation on
     * @param {FormData} [data] Form data to send
     * @param {object} [options] The options to apply to this request, they will be merged with the default options
     * @return {Promise} A promise containing the request
     */
    uploadForm(endpoint: string, data?: object): Promise<any>;

    /**
     * Perform a patch request
     * @param {string} endpoint The url to perform the operation on
     * @param {object} [data] A data object to send as the body of the request
     * @param {object} [options] The options to apply to this request, they will be merged with the default options
     * @return {Promise} A promise containing the request
     */
    patch(endpoint: string, data?: object, options?: object): Promise<any>;

    /**
     * Perform a delete request
     * @param {string} endpoint The url to perform the operation on
     * @param {object} [options] The options to apply to this request, they will be merged with the default options
     * @return {Promise} A promise containing the request
     */
    delete(endpoint: string, options?: object): Promise<any>; 
}
/**
* An interface to describe a location
* @interface
* @public
* @param {number} x The x coordinate
* @param {number} y The y coordinate
* @param {string} [epsg] The EPSG code the x and y are in in the format 'EPSG:<code>'.
* @param {string} [text] Text for the location.

*/
export interface ILocation {
    x: number;
    y: number;
    epsgCode?: string;
    text?: string;
}
/**
* An interface to describe an position and resolution
* @interface
* @public
* @param {number} x The x coordinate
* @param {number} y The y coordinate
* @param {number} resolution The resolution
*/
export interface IInitialView {
    x: number;
    y: number;
    resolution: number;
}
/**
* An interface to describe how to transform a ILocation object to a different type of point object.
* @interface
* @public
*/
export interface IPositionTransformer {
    /**
     * @function
     * @param {ILocation} position The position to transform
     * @returns {object} A new object that represents the same location
     */
    transform(position: ILocation): any;
}
/**
* An interface to describe an extension
* @public
*/
export interface IExtensionObject {
    /**
    * @public
    * @property {string} id The unique identifier for this extension
    */
    id:string;
    /**
    * @public
    * @property {boolean} isActive - Is this extension active
    */
    isActive: boolean;
    /**
    * Activate this extension
    * @public
    * @function
    * @returns {boolean} Returns true if this extension was activated, otherwise false
    */
    activate() : boolean;
    /**
    * Deactivate this extension
    * @public
    * @function
    * @returns {boolean} Returns true if this extension was deactivated, otherwise false
    */
    deactivate() : boolean;
    /**
    * Initialise this extension, will be called by the application
    * @function
    * @param {object} store The vuex store
    * @param {object} i18n The translation object
    * @param {router} i18n The vue-router object
    */
    initialise(store: any, i18n: any, router: any): void;
    /**
     * Allows an extension to append their own URL parameters to an existing list
     * @param params list of parameters that the extension can add to
     * @param options a list of flags that the extension can react to
     * @returns {boolean} a boolean to inform the main application if the list was modified
     */
    appendUrlParams(params: IUrlParam[], options: string[]): boolean;
}
/**
 * describes URL parameters handle by a extension
 * */
export interface IUrlParam {
    name: string,
    value: string
}
/**
 * Allows an extension to handle url parameters
 */
export interface IUrlParamterHandler {
    handleUrlParameters: (...parameters: any[]) => Promise<boolean>;
}
/**
 * Describes an item
 */
export interface IItem {
    /**
    * @property {Object} properties - Item properties.
    * @public
    */
    properties: any;
        /**
    * @property {object} geometry - Feature geometry
    * @public
    */
    geometry: any;
}
/**
 * Describes a feature in Geognosis
 * @interface 
 */
export interface IGeognoSISItem extends IItem {
    /**
    * @property {object} id - Item id
    * @public
    */
    id: any;
    /**
    * @property {number} layerIndex - The index of the layer this item is on
    * @public
    */
    layerIndex: number;
    /**
    * @property {string} sessionId - GeognoSIS Session ID
    * @public
    */
    sessionId: string;
    /**
    * @property {string} mapName - Current map name
    * @public
    */
    mapName: string;

    /**
    * @property {Object} geoJSON - GeoJSON for the item.
    * @public
    */
    geoJSON: any;
    /**
     * @property {string|null} baseUrl The base url for the application
     * @public
     * @readonly
     */
    readonly baseUrl: string;
    /**
     * @property {string} layerId The unique id for the swd this item belongs to
     * @public
     */
    readonly layerId: string;
    /**
     * @property {IAjax} ajaxWrapper An IAjax implementation to perform asynchronous requests
     * @public
     */
    readonly ajaxWrapper: IAjax;
    updateGeometry(newGeoJson: string): Promise<IGeognoSISItem>;
    updateProperties(newValues: any, schemaColumns: ISchemaColumn[]): Promise<IGeognoSISItem>;
    deleteItem(): Promise<IGeognoSISItem> ;
}
/**
 * An interface to describe a spatial query response
 * @interface
 */
export interface IQueryResponse {
    /**
     * @property {IItem[]} [foundItems] The items found during this search
     */
    foundItems?: IGeognoSISItem[];
    /**
     * @property {string} [next] The url to get the next page of results
     */
    next?: string;
    /**
     * @property {string} name The name of the layer these results are from
     */
    name: string;
}
/**
 * An interface to perform a spatial query
 * @interface
 */
export interface IQueryHelper {
    /**
    * Perform a query.<br />
    * @public
    * @param {ol.Map} map - The open layers map.
    * @param {IExpressionBuilder} expressionBuilder - The SIS expression builder to create the expression with.
    * @param {number} maxFeaturesToGet - The maximum number of features to return from the query.
    * @param {number} startPosition - The start position to get items from.
    * @param {Array<number>} [queryBounds] - Limit the query to these bounds, the bounds must be in the same projection as the map. If not supplied the current map extents will be used.
    * @param {boolean} ignoreBbox - Optional parameter to remove the bbox option from the expression
    * @param {AbortController} abort - Optional parameter to abort the request
    * @returns {Promise} A chainable promise containing the results
    */
    findItems(map: any, expressionBuilder: IExpressionBuilder, maxFeaturesToGet: number, startPosition: number, queryBounds?: number[], ignoreBbox?: boolean, abort?: AbortController): Promise<IQueryResponse>;
}
/**
 * A map position
 * @interface
 */
export interface IMapPosition {
    /**
     * @public
     * @property {ILocation} location The location
     */
    location: ILocation;
    /**
     * @public
     * @property {number} zoom The zoom level for this position
     */
    zoom: number;
    /**
     * @public
     * @property {string} projection The EPSG projection code
     */
    projection: string;
}
export interface IRootStateBase {
    /**
    * @public
    * @property {object} ajaxDefaults Defaults for ajax requests
    */
    ajaxDefaults: any;
    /**
    * @public
    * @property {string} baseUrl The base url for the application
    */
    baseUrl: string;
    /**
    * @public
    * @property {number} refreshInterval The interval, in minutes, to refresh the token
    */
    refreshInterval: number;
    /**
    * @public
    * @property {IRefreshToken} token The refresh token
    */
    token: IRefreshToken;
}
export interface IRefreshToken {
    token: string;
    id: string;
}
/**
 * An interface describing the root application state
 * @interface
 */
export interface IRootState extends IRootStateBase{
    /**
     * @public
     * @property {string} mapName The name of the current map
     */
    mapName: string;
    /**
    * @public
    * @property {string} localStorageName The name of our local storage database
    */
    localStorageName: string;
    /**
    * @public
    * @property {Object} loadedLanguages Map of loaded translation files
    */
    loadedLanguages: any;
    /**
    * @public
    * @property {Array<ISupportedCulture>} supportedCultures List of supported cultures
    */
    supportedCultures: ISupportedCulture[];
    /**
    * @public
    * @property {string} currentCulture The name of the current culture
    */
    currentCulture: string;
    /**
    * @public
    * @property {string} userHelpUrl The user help url 
    */
    userHelpUrl: string;
    /**
     * @public
     * @property {boolean} useDarkText use dark text in the interface instead of white
     */ 
    useDarkText: boolean;
    /**
     * @public
     * @property {boolean} showHumanDialog show interactive dialog on startup of map
     */
    showHumanDialog: boolean;
    /**
     * @public
     * @property {string} initialRoute The initial route to set the router to
     */
    initialRoute: string;
    /**
     * @public
     * @property {IUrlParameters} urlParameters The url Parameters
     */
    urlParameters: IUrlParameters;
    /**
     * @public
     * @property {string} cookiePolycyUrl The url to the cookie policy
     */
    cookiePolycyUrl: string;
}

export interface IUrlParameters {
    [extensionId: string]: IUrlParameter;
}
export interface IUrlParameter {
    requiresFullMap: boolean;
    parameters: string[];
}

/**
 * An interface describing available cultures
 * @interface
 */
export interface ISupportedCulture {
    /**
    * @public
    * @property {string} name The name of the culture, e.g. en-gb
    */
    name: string;
    /**
    * @public
    * @property {string} displayName The display text
    */
    displayName: string;
}
/**
 * An interface for extension state
 * @interface
 */
export interface IExtensionState {
    /**
    * @public
    * @property {IExtensionObject[]} extensions Array of extension objects
    */
    extensions: IExtensionObject[];
    /**
    * @public
    * @property {IExtensionObject} defaultExtension The default extension
    */
    defaultExtension: IExtensionObject;
    /**
    * @public
    * @property {IExtensionObject} currentExtension The current extension
    */
    currentExtension: IExtensionObject;
}
/**
 * An interface describing the Geognosis state
 * @interface
 */
export interface IGeognoSisState {
    /**
    * @public
    * @property {object[]} overlays Array of geognosis overlays
    */
    overlays: any[];
    /**
    * @public
    * @property {string} sessionId The geognosis session id
    */
    sessionId: string;
    /**
    * @public
    * @property {string} geognoSisLayerId The id of the geognosis layer
    */
    geognoSisLayerId: string;
    /**
    * @public
    * @property {string} layerEpsgCode The epsg code for the swd
    */
    layerEpsgCode: string;
    /**
    * @public
    * @property {Array.<Object>} [groups] The overlay groups in the swd
    */
    groups?: IGeognoSisGroup[];
    /**
    * @public
    * @property {Array.<number>} [tileSize] The size of the tile to request
    */
    tileSize: number[];
    /**
    * @public
    * @property {number} [sessionTimeout] The time, in milliseconds, that a Geognosis session can be inactive for before it is removed
    */
    sessionTimeout: number;

    /**
    * @public
    * @property {string} [layerIndexPrefix] The prefix used for ordering 
     */
    layerIndexPrefix: string;

    /**
    * @public
    * @property {boolean} [singleTile] Single image vs multiple tiles 
     */
    singleTile: boolean;
    /**
    * @public
    * @property {number} [opacity] Opacity: ranges from 0 (clear) to 1 (solid) 
     */
    opacity: number;
    /**
    * @public
    * @property {Array.<number>} [extent]  optional extent of a layer if the user wants to limit the extent; 
    */
    extent?: number[];

}

export interface IProfileMap {
    /**
    * @public
    * @property {string} [name] The name of the map
    */
    name: string;
    /**
    * @public
    * @property {boolean} [allowAnonymous] Can the map be accessed anonymously
    */
    allowAnonymous: boolean;
}
/**
 * An interface describing profile state for a user
 */
export interface IProfileState {
    /**
    * @public
    * @property {String} [name] The name of the user
    */
    name?: string;
    /**
    * @public
    * @property {IProfileMap[]} maps The maps the user has access to
    */
    maps: IProfileMap[];
    /**
    * @public
    * @property {boolean} canLogout Can the user logout using the current authentication scheme
    */
    canLogout: boolean;
    /**
    * @public
    * @property {boolean} canEditProfile Can the user edit their profile using the current authentication scheme
    */
    canEditProfile: boolean;
    /**
    * @public
    * @property {boolean} showMapSwitcher Can the user view other anonymous maps
    */
    showMapSwitcher: boolean;
}
/**
 * An interface describing a watermark for a map
 */
export interface IWatermark {
    /**
    * @public
    * @property {string} name The name of the watermark
    */
    name: string;
    /**
    * @public
    * @property {string} url The url to the watermark
    */
    url: string;
    /**
    * @public
    * @property {number} opacity The opacity to set on the watermark
    */
    opacity: number;
    /**
    * @public
    * @property {number} width The width to set on the watermark
    */
    width: number;
    /**
    * @public
    * @property {number} height The height to set on the watermark
    */
    height: number;
}
/**
 * An interface describing the current map state
 * @interface
 */
export interface IMapState {
    /**
    * @public
    * @property {object} olMap The open layers map
    */
    olMap: any;
    /**
    * @public
    * @property {ILocation} currentLocation The current selected location
    */
    currentLocation: ILocation;
    /**
    * @public
    * @property {IMapPosition} initialMapState The initial position for the map
    */
    initialMapState: IMapPosition;
    /**
    * @public
    * @property {object[]} baseLayers The base layers in the map
    */
    baseLayers: any[];
    /**
    * @public
    * @property {object} currentBaseLayer The current visible base layer
    */
    currentBaseLayer: any;
    /**
    * @public
    * @property {object[]} dataLayers The data layers for the map
    */
    dataLayers: any[];
    /**
    * @public
    * @property {IWatermark} [watermark] The watermark for the map
    */
    watermark?: IWatermark;
    /**
    * @public
    * @property {number[]} [viewExtent] An array of numbers representing the extent of the view: [minx, miny, maxx, maxy].
    */
    viewExtent?: number[];
    /**
    * @public
    * @property {number} [currentScale] An estimate of the current scale.
    */
    currentScale: number;
    /**
     * @public
     * @property {string} mapTitle The map title
    */
    mapTitle: string;
    /**
     * @public
     * @property {string[]} copyright copyright text to be displayed on the map
     */
    copyright?: string[];
    /**
     * @public
     * @property {number[]} resolutions Resolutions to constrain the map to
     */
    resolutions?: number[];
    /**
     * @public
     * @property {string} initialBaseLayer intitial visible base layer
     */
    initialBaseLayer?: string;
    /**
     * @public
     * @property {string[]} initialDataLayers initial visible data layers
     */
    initialDataLayers?: string[];
    /**
     * @public
     * @property {string[]} attributions Attributions for all layers and overlays combined - see map.vue where they are created.
     */
    attributions: string[];
    /**
    * @public
    * @property {IInitialView} initialLocation The initial location of the map
    */
    initialLocation: IInitialView;
    /**
    * @public
    * @property {Feature} currentFeature The initial location of the map
    */
    currentFeature: any;
}
/**
 *
 */
export interface INavigation {
    /**
    * @public
    * @property {function} [clickHandler] Handler for navigation item click
    */
    clickHandler?: () => Promise<any> ;
}
export interface ILinkItem {
    /**
    * @public
    * @property {Location} [to] Route to navigate to on menu click
    */
    to?: Location;
    /**
    * @public
    * @property {string} [href] Url to navigate to
    */
    href?: string;
    /**
    * @public
    * @property {string} [target] Specify the target attribute.
    */
    target?: string;
}
/**
* An interface for a menu item
* @interface
* @public
*/
export interface INavigationItem extends ILinkItem, INavigation {
    /**
    * @public
    * @property {string} localisationKey The key in the localisation object for the text
    */
    localisationKey: string;
    /**
    * @public
    * @property {string} [cssClasses] Any css classes to apply
    */
    cssClasses?: string;
}
/**
 * Specifies the supported themes
 */
export enum ThemeEnum {
    Light = 0,
    Dark = 1
}
/**
 * Specifies the supported button sizes
 */
export enum ButtonSizeEnum {
    Large = 0,
    Small = 1,
    Normal = 2
}
/**
 * Interface for a navigation button item
 */
export interface INavigationButton extends ILinkItem, INavigation {
    /**
    * @public
    * @property {string} icon The icon to use on the button, one of the material ui icon names see https://materialdesignicons.com/
    */
    icon: string;

    /**
     * @property {string} color Applies specified color to the control - it can be the name of material color (for example success or purple) or css color (#033 or rgba(255, 0, 0, 0.5))
     */
    color: string;

    /**
    * @public
    * @property {ThemeEnum} theme The theme to use for the button
    */
    theme: ThemeEnum;

    /**
     * @public
     * @property {ButtonSizeEnum} size The size of the button
     */
    size: ButtonSizeEnum;

    /**
    * @public
    * @property {boolean} showLoading Should the button show a loading indicator.
    */
    showLoading: boolean;

    /**
    * @public
    * @property {string} [cssClasses] Any css classes to apply
    */
    cssClasses?: string;

    /**
     * @public
     * @property {string} The aria label key inside en-gb.json, used on the buttons
     */
    ariaLabelKey: string;

    /**
     * @public
     * @property {string} The tooltip key inside en-gb.json, used on the buttons
     */
    tooltipKey: string;
}
/**
 * Application ui state
 * @interface
 * @public
 */
export interface IUIState {
    /**
    * @public
    * @property {string} activityMessage Message for the activity snackbar.
    */
    activityMessage: string;
    /**
    * @public
    * @property {string} errorMessage Message for the error snackbar.
    */
    errorMessage: string;
    /**
    * @public
    * @property {string} notificationMessage Message for the notification snackbar.
    */
    notificationMessage: string;
    /**
    * @public
    * @property {boolean} showNotification Show the notification snackbar.
    */
    showNotification: boolean;
}
/**
 * Map Application ui state
 * @interface
 * @public
 */
export interface IMapUiState extends IUIState {
    /**
    * @public
    * @property {boolean} navDrawerVisible Is the application navigation drawer visible.
    */
    navDrawerVisible: boolean;
    /**
    * @public
    * @property {boolean} dialogVisible Is the application dialog visible.
    */
    dialogVisible: boolean;
    /**
    * @public
    * @property {boolean} appPanelVisible Is the application panel visible.
    */
    appPanelVisible: boolean;
    /**
    * @public
    * @property {INavigationItem[]} navigationItems Navigation Items for the navigation drawer.
    */
    navigationItems: INavigationItem[];
    /**
    * @public
    * @property {INavigationButton[]} contentControls Control items for the application content.
    */
    contentControls: INavigationButton[];
    /**
    * @public
    * @property {INavigationButton[]} speedDialItems Speed dial items.
    */
    speedDialItems: INavigationButton[];
    /**
    * @public
    * @property {INavigationButton[]} toolstripItems Items for the toolstrip.
    */
    toolstripItems: INavigationButton[];
    /**
    * @public
    * @property {boolean} checkedIamHuman preventing repeats.
    */
    checkedIamHuman: boolean;
}
export interface IInteractiveSnackbar {
    message: string;
    show: boolean;
    buttonText: string;
    showActivity: boolean;
    callback: () => void;
}
/**
 * Specifies the uncapable error code
 */
export enum GeoLocatorErrorEnum {
    UnCapable = 10000
}
/**
 * An interface to manage cookies
 */
export interface ICookieManager {
    /** Creates a cookie
    * @public
    * @function
    * @param {string} name - The identifier of the cookie to be set
    * @param {string} value - The value to be stored, this can be a string representation of an object 
    * @param {mumber} days - The number of days to store this cookie
    */
    create(name: string, value: string, days: number): void;
    /** Reads a cookie
    * @public
    * @function
    * @param {string} name - The identifier of the cookie to be set
    * @returns {string} The value of the previously stored cookie or null
    */
    read(name: string): string;
    /** Remove a previously set cookie
    * @public
    * @function
    * @param {string} name - The identifier of the cookie to be erase
    */
    eraseCookie(name: string): void;
}
/**
 * An interface to manage user consent to cookies being stored on their device
 */
export interface ICookieConsent {
    /** Indicates whether the user has agreed to cookies being stored.  
    * @public
    * @function
    * @returns {boolean} Returns true if the user has accepted cookies, otherwise false
    */
    haveConsent(): boolean;
    /** Called when the user has agreed to cookies being stored  
    * @public
    * @function
    */
    setConsent(): void;
    /** Removes user consent to store cookies 
    * @export
    * @function
    */
    removeConsent(): void;
}
/**
 * An interface describing a bookmark
 */
export interface IBookmark  {
    /**
     * @public
     * @property {ILocation} location The location
     */
    location: ILocation;
    /**
     * @public
     * @property {number} resolution The resolution for this position
     */
    resolution: number;
    /**
    * @public
    * @property {number} [id] The unique id for the item
    */
    id?: number;
    /**
    * @public
    * @property {string} name The name of the bookmark
    */
    name: string;
    /**
    * @public
    * @property {boolean} isHome Is this book mark the home bookmark
    */
    isHome: boolean;
    /**
    * @public
    * @property {boolean} isUserCreated Was this bookmark created by a user
    */
    isUserCreated: boolean;
}
/**
 * An interface for bookmark state
 */
export interface IBookmarkState {
    /**
    * @public
    * @property {IBookmark[]} bookmarks The bookmarks
    */
    bookmarks: IBookmark[];
    /**
    * @public
    * @property {boolean} initialised Has the state been initialised
    */
    initialised: boolean;
}
/**
 * An interface to describe a Feature info result
 * @interface
 */
export interface IFeatureInfoResult
{
    /**
     * @property {string} name The name of the layer these results are from
     */
    name: string;
    /**
     * @property {IItem[]} [features] The features found
     */
    features?: IItem[];
}
/**
 * An interface for creating a component
 * @interface 
 */
export interface ITemplateFactory {
    /**
     * Create a component
     * @function
     * @param {string} name The name of the template 
     * @param {string} html The html portion of the template
     * @param {string} json The json potion of the template
     * @param {string[]} props The props that the template will be supplied
     * @return {ITemplate} The compiled template
     */
    createComponent(name:string, html:string, json:string, props:string[]): ITemplate;
}
/**
 * An interface for custom templates
 * @interface
 */
export interface ITemplate {
    /**
     * @property {any} component The vue component
     */
    component: any;
    /**
     * @property {string} name The name of the template
     */
    name:string;
}
/**
 * Feature information state
 * @interface
 */
export interface IFeatureInformationState {
    /**
     * @property {IFeatureInfoResult[]} results The results of the feature info query
     */
    results: IFeatureInfoResult[];
    /**
     * @property {ITemplate[]} templates Array of result templates
     */
    templates: ITemplate[];
    /**
     * @property {string} highlightLayer The name of the layer that is currently being highlighted
    */
    highlightLayer: string;
    /**
     * @property {ISelectedFeature} selectedFeature The feature selected in the overlay
    */
    selectedFeature: ISelectedFeature;
}
export interface ISelectedFeature {
    /**
     * @property {number} selectedFeatureIndex The index of the feature selected
    */
    selectedFeatureIndex: number;
    /**
     * @property {string} selectedFeatureOverlay The name of the overlay the selected feature is on
    */
    selectedFeatureOverlay: string;
}
/**
 * Drawing extension state
 * @interface
 */
export interface IDrawingExtensionState {
    /**
     * @property {string} layerName The name of the drawing layer
     */
    layerName: string;
    /**
     * @property {IDrawingStyle} styles The map styles for the drawing extension
     */
    styles: IDrawingStyle;
    /**
     * @property {IDrawingInteractions} interactions The map interactions for the drawing extensions
     */
    interactions: IDrawingInteractions;
    /**
     * @property {Object} selectedFeatures Collection of selected features
     */
    selectedFeatures: any;
    previousInteraction: any;
    showMenu: boolean;
    currentStyle: any;
    icons: IPreviewIcon[];
    selectedIcon: IPreviewIcon;
    pointPreviewType: PointPreviewTypeEnum
}
export enum PointPreviewTypeEnum {
    Style= 'style',
    Icon='icon'
}
/**
 * Drawing styles
 * @interface
 */
export interface IDrawingStyle {
    /**
     * @property {object} polygon The map style for lines and polygons
     */
    polygon: any;
    /**
     * @property {object} point The map style for points
     */
    point: any;
    /**
     * @property {object} text The map style for text
     */
    text: any;
    /**
     * @property {object} select The map style selected items
     */
    select: any;
}
/**
 * Drawing interactions
 * @interface
 */
export interface IDrawingInteractions {
    /**
    * @property {object} polygon The map interaction to draw polygons
    */
    polygon: any;
    /**
    * @property {object} line The map interaction to draw lines
    */
    line: any;
    /**
    * @property {object} arrow The map interaction to draw an arrow
    */
    arrow: any;
    /**
    * @property {object} line The map interaction to draw circles
    */
    circle: any;
    /**
    * @property {object} line The map interaction to draw circles with a specified radius
    */
    radiusCircle: any;
    /**
    * @property {object} point The map interaction to draw points
    */
    point: any;
    /**
    * @property {object} text The map interaction to draw text
    */
    text: any;
    /**
    * @property {object} select The map interaction to select a previously drawn feature
    */
    select: any;
    /**
     * @property {object} modify The map interaction modify a previously selected feature
     */
    modify: any;
    /**
     * @property {object} translate The map interaction to move a previously selected feature
     */
    translate: any;
    /**
     * @property {object} boxSelect The map interaction to select features with a dragable box
     */
    boxSelect: any;
}
/**
 * A response from a request to get style information
 * @interface
 */
export interface IStyleSetup {
    /**
    * @property {object[]} dashStyle The available dash styles
    */
    dashStyle: IDashStyle[];
}
/**
 * Dash style definition
 * @interface
 */
export interface IDashStyle {
    /**
    * @property {string} id The id for this style
    */
    id: string;
    /**
    * @property {number[]} definition The definition for the style see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/setLineDash for more info
    */
    definition: number[];
    /**
    * @property {string} imageUrl Url to an image that represents this style
    */
    imageUrl: string;
}
/**
 * Pdf state
 * @interface
 */
export interface IPdfState {
    /**
     * @property {Object} printTemplates The available print templates
     */
    printTemplates: IPrintTemplateItem[];
    /**
     * @property {string} printPreviewSessionId The print session id for the preview
     */
    printPreviewSessionId: string;
    /**
    * @property {Object} currentTemplate The current print template
    */
    currentTemplate: IPrintTemplateItem;
    /**
    * @property {Boolean} previewOpen Controls the visibility of the template preview dialog
    */
    previewOpen: boolean;
    /**
    * @property {Object} annotationLayer The geognosis layer to draw user vectors into
    */
    annotationLayer: any;
    /**
    * @property {Object} pdfParams Any additional pdf parameters
    */
    pdfParams: any;
    /**
    * @property {string} previewUrl The url to the print preview
    */
    previewUrl: string;
    /**
    * @property {string} previewScale The scale required to get a pdf of the current preview
    */
    previewScale: string;
    /**
    * @property {number} previewResolution The resolution of the current preview
    */
    previewResolution: number;
    /**
    * @property {IPrintScaleBar} previewScalebar The calculated sizes for the scalebar
    */
    previewScalebar: IPrintScaleBar;
}
/**
 * A print template item
 * @interface
 */
export interface IPrintTemplateItem {
    /**
     * @property {string} displayName The display name for this item
     */
    displayName: string;
    /**
     * @property {string} name The unique name for this item
     */
    name: string;
    /**
     * @property {Object} [children] The child items
     */
    children?: IPrintTemplateItem[];
    /**
     * @property {Object} [prompts] The prompt items for this template
     */
    prompts?: IPrintTemplatePrompt[];
    /**
    * @property {string} [paperSize] The size of the paper, e.g. A3, A4, etc
    */
    paperSize?: string;
    /**
    * @property {boolean} [landscape] The orientation of the paper
    */
    landscape?: boolean;
    /**
    * @property {number[]} [mapFrameSize] The size of the map frame
    */
    mapFrameSize?: number[];
    /**
    * @property {number[]} [mapFrameOrigin] The origin of the map frame
    */
    mapFrameOrigin?: number[];
}
export interface IPrintTemplatePrompt {
    /**
    * @property {string} name The name of the prompt
    */
    name: string;
    /**
    * @property {string} text The text for the prompt
    */
    text: string;
}
export interface IThemeLegendSize {
    /**
     * @property {number} width The width of a legend
     */
    width: number;
    /**
    * @property {number} height The height of a legend
    */
    height: number; 
}
export interface IPrintScaleBar {
    /**
    * @property {number} printTemplateWidth The actual width of the scale bar to be drawn on the print template
    */
    printTemplateWidth: number;
    /**
    * @property {number} width The width value of the scale bar
    */
    width: number;
}
/**
 * An interface to represent a key value pair
 */
export interface IKeyValue {
    /**
    * @property {string} key The key
    */
    key: string;
    /**
    * @property {Object} value The value
    */
    value: any;
}
/**
 * An interface that describes a theme
 * @interface
 */
export interface ITheme {
    /**
    * @property {number} index The index of the theme
    */
    index: number;
    /**
    * @property {string} name The title for the theme
    */
    title: string;
    /**
    * @property {string} type The type of theme
    */
    type: string;
    /**
    * @property {boolean} disabled Is the theme disabled
    */
    disabled: boolean;
    /**
    * @property {boolean} [disabled] Is the theme added by the user
    */
    userAdded?: boolean;
    /**
    * @property {Object} [components] The components that make up this theme
    */
    components?: IThemeComponent[];
    /**
    * Sets the disabled state of the theme
    * @export
    * @param {boolean} disabled The required state to be set
    * @returns {Promise} A chainable promise
    */
    setDisabled(disabled: boolean): Promise<any>;
    /**
    * Gets the base url to the theme legend
    * @export
    * @returns {string} the url
    */
    themeLegendUrl();
    /**
    * @property {INewTheme} [definition] The definition used to create the theme
    */
    definition?: INewTheme;
}
/**
 * An interface that describes a theme component
 * @interface
 */
export interface IThemeComponent {
    /**
    * @property {number} index The index of the component
    */
    index: number;
    /**
    * @property {string} name The title for the component
    */
    title: string;
    /**
    * @property {boolean} disabled Is the component disabled
    */
    disabled: boolean;
    /**
    * Sets the disabled state of the component
    * @export
    * @param {boolean} disabled The required state to be set
    * @returns {Promise} A chainable promise 
    */
    setDisabled(disabled: boolean): Promise<any>;
}
/**
 * An interface that describes a group of overlays in a GeognoSIS http layer
 * @interface
 */
export interface IGeognoSisGroup {
    /**
    * @property {number} start The index of the first overlay in the group
    */
    start: number;
    /**
    * @property {number} end The index of the last overlay in the group
    */
    end: number;
    /**
    * @property {string} name The name of the group
    */
    name: string;
}
export interface IFontSizeSelectorConfig {
    min: number;
    max: number;
    step:number;
}
export interface IListItem {
    displayName: string;
    children?:IListItem[];
}
export interface IOverlayListItem extends IListItem{
    imageUrl: string;
    layer: any;
}
export interface IThemeListItem extends IListItem {
    theme: ITheme;
}
export interface IGroupListItem extends IListItem {
    group: IGeognoSisGroup;
}
export interface INewThemeComponent {
    propertyMap: {};
}
export interface INewTheme {
    type: string;
    components?: INewThemeComponent[];
    propertyMap?: {};
    index?: number;
}
export interface IKeyValuePair {
    key: string;
    value: any;
}
/**
 * Constructor to use for the client search object
 * @interface
 */
export interface ISearchConstructor {
    new(config: ISearchConfig): ISearch;
}
/**
 * Extended configuration options
 */
export interface IExtendedSearchConfig {
    /**
    * @public
    * @property {function} searchConstructor The constructor to use to create the ISearch object
    */
    searchConstructor: ISearchConstructor;

    [keys: string]: any;
}
/**
 * Configuration for a search
 * @interface
 */
export interface ISearchConfig {
    /**
    * @public
    * @property {string} name The name of the search
    */
    name: string;
    /**
    * @public
    * @property {string} id The unique id for the search
    */
    id: string;
    /**
    * @public
    * @property {string} searchEndpoint The endpoint where the search will be performed
    */
    searchEndpoint: string;
    /**
    * @public
    * @property {IExtendedSearchConfig} extendedConfiguration Extended search configuration to create the search object
    */
    extendedConfiguration: IExtendedSearchConfig;
    /**
    * @public
    * @property {IAjax} ajax The IAjax object to query the data source with
    */
    ajax: IAjax;
    /**
    * @public
    * @property {number} zoomLevel The zoom level to go to when a result is selected
    */
    zoomLevel: number;
}
/**
 * A result for a search
 * @interface
 */
export interface ISearchResult {
    /**
    * @public
    * @property {number} x The x coordinate for the result
    */
    x: number;
    /**
    * @public
    * @property {number} y The y coordinate for the result
    */
    y: number;
    /**
    * @public
    * @property {string} result The result text to display
    */
    result: string;
    /**
    * @public
    * @property {Map} [additionalColumns] key value pairs of additional results
    */
    additionalColumns?: Map<string, any>;
    /**
     * @public
     * @property {ISearchResultAction} [action] Defines an action to be perfomed when a result is clicked
     */
    action?: ISearchResultAction;

}
export interface ISearchResultAction {
    /**
     * The action to perform
     * @param result
     * @returns
     */
    customClickHandler?: (result: ISearchResult) => Promise<ISearchResponse>; 
    /**
     * @public
     * @property {string} [icon] The icon to display
     */
    icon?: string;
}
/**
 * A response from a search
 * @interface
 */
export interface ISearchResponse {
    /**
    * @public
    * @property { ISearchResult[]} [results] The results od the search
    */
    results?: ISearchResult[];
    /**
    * @public
    * @property { boolean} moreAvailable Are there more results available
    */
    moreAvailable: boolean;
    /**
    * @public
    * @property { number} crs The projection the results are in
    */
    crs: number;
    /**
    * @public
    * @property { boolean} errorState Did an error occur during the search
    */
    errorState: boolean;
    /**
    * @public
    * @property {string} id The unique id for the search
    */
    id: string;
    /**
    * @public
    * @property {string} name The name of the search
    */
    name: string;
}
/**
 * A search interface to get results back from a data source
 * @interface
 */
export interface ISearch {
    /**
     * Perform a search on a data source
     * @function
     * @param {string} filter The search filter
     * @param {number} start The index of where to start the search
     * @param {number} noOfResultsToGet The amount of results to get in this search
     * @returns {Promise} A chainable promise containing the results
     */
    search: (filter: string, start: number, noOfResultsToGet: number) => Promise<ISearchResponse>;
    /**
    * @public
    * @property {string} id The unique id for the search
    */
    id: string;
    /**
    * @public
    * @property {string} name The name of the search
    */
    name: string;
}
/**
 * A factory interface to create ISearch objects
 * @interface
 */
export interface ISearchFactory {
    /**
    * Creates a search object
    * @function
    * @param {ISearchConfig} config The configuration for the search
    * @returns {ISearch} A configured search
    */
    createSearch(config: ISearchConfig): ISearch;
}
export interface IManagerOptions {
    Miniature: string;
}
/**
 * An interface describimg a map summary
 * @interface
 */
export interface IMapSummary {
    /**
    * @public
    * @property {string} name The name of the map
    */
    name: string;
    /**
    * @public
    * @property {string} description A description for the map
    */
    description: string;

    managerOptions: IManagerOptions;
}
/**
 * An interface for the password rules
 * @interface
 * */
export interface IResetPasswordRulesModel {
    /**
    * @public
    * @property {string} userName The name of the user to reset the password
    */
    userName: string;
    /**
    * @public
    * @property {string} code TThe reset code
    */
    code: string;
    /**
    * @public
    * @property {number} minLength The minimum length of the password
    */
    minLength: number;
    /**
    * @public
    * @property {boolean} requireDigit Require the password to contain a digit
    */
    requireDigit: boolean;
    /**
    * @public
    * @property {boolean} requireLowercase Require the password to have a lower case character
    */
    requireLowercase: boolean;
    /**
    * @public
    * @property {boolean} requireNonAlphanumeric Require the password to have a non alphanumeric character
    */
    requireNonAlphanumeric: boolean;
    /**
    * @public
    * @property {boolean} require Require the password to have an uppercase character
    */
    requireUppercase: boolean;
}
/**
 * An interface to describe a colour
 * @interface
 */
export interface IPreviewColour {
    /**
    * @public
    * @property {number} r The red component of the colour
    */
    r: number;
    /**
    * @public
    * @property {number} g The green component of the colour
    */
    g: number;
    /**
    * @public
    * @property {number} b The blue component of the colour
    */
    b: number;
    /**
    * @public
    * @property {number} a The alpha channel transparency of the colour
    */
    a: number;

}
/**
 * In interface to describe a stroke style
 * @interface
 */
export interface IPreviewStroke extends IPreviewColour {
    /**
    * @public
    * @property {number} w The width of the stroke
    */
    w: number;
    /**
    * @public
    * @property {number[]} [dash] Line dash pattern
    */
    dash?: number[];
    /**
    * @public
    * @property {string} [lineCap] Line cap style
    */
    lineCap?: string;
    /**
    * @public
    * @property {string} [lineJoin] Line join style
    */
    lineJoin?: string;
}
/**
* An interface to describe a text style
* @interface
*/
export interface IPreviewText {
    /**
    * @public
    * @property {string} font The font for the text
    */
    font: string;
    /**
    * @public
    * @property {string} text The test to display
    */
    text: string;
}
/**
 * An interface to describe a text style
 * @interface
 */
export interface IPreviewPoint {
    /**
    * @public
    * @property {number} radius The radius of the point
    */
    radius: number;
    /**
    * @public
    * @property {IPreviewIcon[]} icons The icons that can be used for the point
    */
    icons?: IPreviewIcon[];
}
export interface IPreviewIcon {
    id: string;
    name: string;
    offsetX: number;
    offsetY: number;
}
export interface IPreviewIconWithImage extends IPreviewIcon {
    image: HTMLImageElement;
}
/**
 * An interface to describe a preview style
 * @interface
 */
export interface IPreviewStyle {
    /**
    * @public
    * @property {IPreviewStroke} stroke The stroke for the style
    */
    stroke: IPreviewStroke;
    /**
    * @public
    * @property {IPreviewColour} fill The fill for the style
    */
    fill: IPreviewColour;
    /**
    * @public
    * @property {IPreviewText} [text] The text for the style
    */
    text?: IPreviewText;
    /**
    * @public
    * @property {IPreviewPoint} [point] The point for the style
    */
    point?: IPreviewPoint;
}

/** TODO: This interface is duplicated in the Editing extension's abstractions. Amend the Editing extension to point here instead? */
export interface IEditingPreviewStyle {  
    point: IPreviewStyle;
    polygon: IPreviewStyle;
}

export interface IPostgisManagerState {

}
export interface ISQLServerManagerState {

}
export interface IOracleManagerState {

}
export interface IOSManagerState {
    
}
export interface IAAState {

}
export interface ICulture extends ISupportedCulture{
    data: object;
}
export interface ICultureCache {
    [type: string]: ICulture;
}
export interface ICultureState {
    cache: ICultureCache;
    currentCulture: ICulture;
    allowedCultures: ISupportedCulture[];
}
export interface INewCulture extends ISupportedCulture{
    image: File;
    data: File;
}
export interface IFileEvent {
    files: File[];
    onError: (message: string) => void;
    clearErrors: () => void;
}
export interface IFileInfo {
    id: string;
    name: string;
    url: string;
}
export interface ITableHeader {
    text: string;
    value: string;
    align: string;
    sortable: boolean;
    type: number;
    typeName: string;
    sort?: (a: any, b: any) => number;
    closable: boolean;
    filterable: boolean;
    allowedFilters: FilterTypeEnum;
}
export enum FilterTypeEnum {
    None = 0,
    Contains = 1,               //000001
    Equals = 1 << 1,            //000010
    GreaterThan = 1 << 2,       //000100
    GreaterThanEquals = 1 << 3, //001000
    LessThan = 1 << 4,          //010000
    LessThanEquals = 1 << 5,    //100000
}
export interface ITableFilter {
    filterType: FilterTypeEnum;
    searchColumn: ITableHeader;
    value: any;
}

/**
 * An interface to describe what content should be passed into the about component
 * @interface
 */
export interface IAboutContent {
    /**
    * @public
    * @property {string} title Is the title of about content
    */
    title: string,
    /**
    * @public
    * @property {string} titleDescription Is the title description of about content
    */
    titleDescription: string 
}
export const DotPattern: number[] = [2, 1];
export const DashPattern: number[] = [10, 10];



